/**
 * Editorial content for priority city landing pages.
 *
 * Plain prose only — no marketing language, no invented statistics.
 * The copy is purposefully factual and calm because these pages are the
 * trust anchor of the directory.
 *
 * If a city slug is missing from this map, the city page silently hides
 * its editorial section. That keeps non-priority cities lean while
 * priority pages (Berlin, Munich, Hamburg, Frankfurt, Vienna, Zurich)
 * carry the editorial weight required to justify indexation.
 */

export const CITY_EDITORIAL = {
  berlin: {
    intro: [
      "Berlin is one of Europe's most active hubs for civic engagement. The city's nonprofit landscape spans large historical organisations such as Caritas, Diakonie and the Berliner Stadtmission, alongside a dense network of neighbourhood initiatives — from Kiezhilfe in Neukölln and Wedding to refugee-support projects across the city.",
      "Whether you want to volunteer a few hours each week, donate to a listed local cause or contribute professional skills pro bono, Berlin offers a broad range of practical ways to take part. Many initiatives are well-suited to newcomers and welcome both German- and English-speaking volunteers.",
      "The opportunities on doing-good.org are gathered from public NGO platforms (betterplace.org, vostel.de) and a curated list of established Berlin charities. Each project is reviewed before publishing and updated daily.",
    ],
  },
  munich: {
    intro: [
      "Munich combines deep-rooted Catholic and civic charities — Caritas, Diakonie, the Münchner Tafel — with a growing scene of social-impact projects in education, climate and refugee support. Whether you live in the city centre, in one of the surrounding districts or are visiting for a few months, there are concrete ways to contribute time, money or expertise.",
      "Many Munich-based organisations are well-organised and welcome long-term as well as short-term volunteers. Pro bono opportunities are common in legal advice, language tutoring and IT support.",
      "All opportunities listed here come from public sources and link directly to the organisation behind them — there are no fees, no commissions and no intermediaries.",
    ],
  },
  hamburg: {
    intro: [
      "Hamburg's nonprofit sector is shaped by its port history and its strong tradition of civic responsibility. Established institutions such as Hamburger Tafel, Diakonie and Hinz&Kunzt sit alongside grassroots initiatives in homelessness support, harbour-area community projects and migrant integration.",
      "If you live in Hamburg or the surrounding region, you can volunteer locally, donate to a listed Hamburg organisation or contribute professional skills — many initiatives explicitly look for legal, design or language support.",
      "The opportunities on this page are aggregated from public NGO directories and a curated list of established Hamburg charities, reviewed before publishing and updated daily.",
    ],
  },
  frankfurt: {
    intro: [
      "Frankfurt's role as Germany's financial and infrastructural centre is matched by an unusually international civic landscape. Organisations supporting refugees, language education and social inclusion are particularly visible, alongside the city's larger welfare institutions.",
      "If you live in or commute to Frankfurt, there are practical ways to help — volunteer with a neighbourhood initiative, donate to a listed local organisation or contribute legal, financial or language skills pro bono.",
      "Each opportunity listed here links directly to the organisation behind it. We don't take donations, we don't take commissions and we don't run programmes — we only make local ways to help easier to find.",
    ],
  },
  // ---- Thin-priority cities — extended editorial (~250-350 words) ----
  vienna: {
    intro: [
      "Vienna's nonprofit landscape is built around a small number of large, well-established welfare organisations — Caritas Wien, Diakonie Österreich, Volkshilfe and the Wiener Hilfswerk — together with a growing layer of neighbourhood initiatives focused on refugee integration, homelessness support and climate action. Compared to larger German cities, the directory currently lists a smaller number of Vienna-based opportunities. We deliberately keep this page lean: rather than padding it with unrelated projects, we only list opportunities from organisations whose work is publicly verifiable.",
      "If you live in Vienna or one of its 23 Bezirke, there are concrete ways to take part. The most common entry points are volunteering a few hours a week at neighbourhood projects, making one-off or regular donations to listed Austrian organisations, or offering professional skills pro bono — legal counsel, language tutoring (Deutschkurse), design or accounting support are all in steady demand.",
      "Many Vienna-based organisations welcome both German- and English-speaking volunteers. If you have flexible time, longer-term commitments (six months or more) are particularly valued in refugee-support and homelessness projects, where consistency matters more than total hours.",
      "Listings on this page come from public NGO platforms (betterplace.org, vostel.de) and a curated list of established Austrian charities. Each project is reviewed before publishing — we check that the organisation is real, publicly identifiable and not a duplicate listing. We do not endorse individual projects beyond confirming the source. If you run a Vienna-based nonprofit and would like to be listed, you can submit your opportunity directly through this site, free of charge.",
    ],
  },
  zurich: {
    intro: [
      "Zurich is Switzerland's largest city and a quiet but consistent centre of civic engagement. Its nonprofit landscape mixes established welfare institutions — Caritas Schweiz, Schweizerisches Rotes Kreuz, Heilsarmee, Pro Senectute — with smaller initiatives focused on refugee integration, urban homelessness, climate action and youth education. The directory currently lists a smaller number of Zurich-based opportunities than German hubs of similar size; rather than dilute the page with unrelated listings, we keep the surface small and only include opportunities whose source organisations are publicly verifiable.",
      "If you live in Zurich or the surrounding Kanton, there are several practical ways to help. The most common are: volunteering locally a few hours per week with a listed organisation, making a one-off or recurring donation to a registered Swiss organisation, or offering professional skills pro bono. Legal advice, tutoring (Deutschkurse for newcomers), bookkeeping and design are particularly in demand at smaller initiatives.",
      "Many Zurich-based organisations welcome volunteers in both German and English. Where a project explicitly requires Swiss German or French, the listing usually mentions it. If you plan to donate, check the organization's own donation page for details about receipts, tax information and accepted payment methods.",
      "All listings on this page link directly to the source organisation. doing-good.org does not process donations, take commissions or act as an intermediary. Listings are aggregated from public NGO platforms and a curated list of established Swiss charities, reviewed before publishing and refreshed daily. If you run a Zurich-based nonprofit and would like to appear here, you can submit your opportunity at no cost.",
    ],
  },
};

/**
 * 6 priority cities the directory actively maintains.
 * Order is the canonical browse order used in "Other cities you might explore".
 */
export const PRIORITY_CITIES = [
  { slug: "berlin", name: "Berlin", country: "Germany" },
  { slug: "munich", name: "Munich", country: "Germany" },
  { slug: "hamburg", name: "Hamburg", country: "Germany" },
  { slug: "frankfurt", name: "Frankfurt", country: "Germany" },
  { slug: "vienna", name: "Vienna", country: "Austria" },
  { slug: "zurich", name: "Zurich", country: "Switzerland" },
];

/**
 * Standard FAQ items used on every priority city page. The component
 * interpolates `{{city}}` into the questions and answers at render time
 * so each page gets unique, crawlable text.
 */
export const CITY_FAQ = [
  {
    q: "How can I volunteer in {{city}}?",
    a: "Browse the opportunities on this page and click through to the organisation you'd like to join. Most listings include direct application or contact details. There is no doing-good.org middle step — you sign up with the organisation directly.",
  },
  {
    q: "Can I donate to {{city}} organisations instead of volunteering?",
    a: "Yes. Many listed opportunities are donation campaigns from local organisations. Donations go directly to the organisation through their own page — we don't process or hold funds.",
  },
  {
    q: "How are {{city}} opportunities selected for this directory?",
    a: "Each listed organisation is checked to make sure it is a real, publicly identifiable NGO or community initiative. We don't endorse individual projects beyond confirming the source, but we exclude duplicates and unverifiable entries.",
  },
  {
    q: "Can I support {{city}} organisations remotely?",
    a: "Yes — many opportunities, especially donations and some pro bono services (legal advice, design, translation, tutoring online), can be offered remotely. Look for listings tagged 'pro bono' or 'donation' if you can't be physically present.",
  },
  {
    q: "How often are opportunities updated?",
    a: "The directory refreshes daily from our sources. New opportunities appear within 24 hours of being published on the source platform; listings that are removed or expired are filtered out automatically.",
  },
  {
    q: "I run a nonprofit in {{city}} — can we be listed?",
    a: "Yes, listings are free. Use the 'For organizations' link in the header to submit your opportunity, or write to info@doing-good.org. We review submissions to keep the directory trustworthy.",
  },
];

/**
 * "Ways to help" cards shown on every priority city page.
 * Bound to existing /offers filters so each card lands the user
 * on a useful filtered list.
 */
export const WAYS_TO_HELP = [
  {
    key: "volunteer",
    label: "Volunteer locally",
    body: "Join an organisation in person for a few hours a week.",
    category: "volunteering",
  },
  {
    key: "donate",
    label: "Donate to a listed cause",
    body: "Support a listed local organisation through their own page.",
    category: "donation",
  },
  {
    key: "fundraise",
    label: "Support a campaign",
    body: "Back a current fundraising effort run by a local organisation.",
    category: "fundraising",
  },
  {
    key: "pro_bono",
    label: "Offer professional skills",
    body: "Contribute legal, design, language or technical support pro bono.",
    category: "pro_bono_service",
  },
];

export function getCityEditorial(slug) {
  return CITY_EDITORIAL[String(slug || "").toLowerCase()] || null;
}
